import React, { useState } from 'react'
import Icon from 'blocks.simple/icon/icon'
import TouchEditor from 'blocks.app/touchEditor/touchEditor'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import translate from 'core/translate'
import styles from './editor__events.jcss'
import item from 'blocks.simple/item/item.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import { editor } from '../editor.local.methods'

export type Event = {
    key: string
    id: string
    eventIndex: number
    eventCondition: { key: string; value: string }
    eventType: string
}

interface IState {
    isOpenModal: boolean
    action?: string
    event?: Event
    createMode?: boolean
}

const ListItem = (p_: {
    condition: { key: string; value: string }
    type: string
    onEdit: () => void
    onDelete: () => void
}) => (
    <div className={styles.action}>
        <div className={styles.eventTitle}>{translate(p_.type)}</div>
        <div className={styles.eventIcons}>
            <Icon
                containerClass={grid.mr_mini}
                type={'pencil'}
                tooltip={{ title: translate('edit') }}
                onClick={p_.onEdit}
            />
            <Icon type={'delete'} tooltip={{ title: translate('delete') }} onClick={p_.onDelete} />
        </div>
    </div>
)

const getPageOptions = (pages: { order: number; name: string }[]) => {
    return pages.map((page) => ({
        id: page.order,
        name: `${translate('page')} ${page.order + 1}`,
    }))
}

const EditorEvents = (p_: {
    page: {
        id: number
        name: string
        order: number
        playOnEvent: {
            events: {
                id: string
                eventType: string
                key: string
                eventCondition: { key: string; value: string }
            }[]
        }
        nextPageOrder: number
        broadcastEvents: { id: number; name: string }[]
        repeatedEventHandlingStrategy: 'ignore' | 'reload'
    }
    pages: { order: number; name: string }[]
}) => {
    const [state, setState] = useState<IState>({
        isOpenModal: false,
        createMode: false,
        action: undefined,
        event: undefined,
    })

    const onSave = (event: Event, eventIndex?: number) => {
        let data: any = {}

        if (!p_.page.playOnEvent) {
            data = {
                compositionType: 'OR',
                events: [event],
            }
        } else {
            data = p_.page.playOnEvent
            if (typeof eventIndex === 'number') {
                data.events[eventIndex] = event
            } else {
                data.events.push(event)
            }
        }
        setState((prevState) => {
            return {
                ...prevState,
                isOpenModal: false,
                createMode: false,
            }
        })
        // @ts-ignore
        editor.setEventsToPage(data)
    }
    const onDelete = (index: number) => {
        // @ts-ignore
        editor.deleteEventFromPage(index)
    }
    const onChangePage = ({ id }: { id: string }) => {
        // @ts-ignore
        editor.setNextPage(id)
    }

    const onSaveSelectedEvent = (event: { id: number; name: string }) => {
        setState((prevState) => {
            return {
                ...prevState,
                isOpenModal: false,
                createMode: false,
            }
        })
        // @ts-ignore
        editor.setBroadcastEventsToPage(event)
    }

    const deleteEvent = (id: number) => {
        // @ts-ignore
        editor.deleteEvent(id)
    }

    const s_ = state

    const showRepeatEventStrategy =
        (p_.page.playOnEvent && p_.page.playOnEvent.events.length) ||
        (p_.page.broadcastEvents && p_.page.broadcastEvents.length)

    return (
        <div className={styles.eventsWrapper}>
            <div className={styles.title}>
                <div className={item.ellipsis}>{translate('events')}</div>
                <div
                    className={styles.icon}
                    onClick={() =>
                        setState((prevState) => {
                            return {
                                ...prevState,
                                isOpenModal: true,
                                createMode: true,
                                action: undefined,
                            }
                        })
                    }
                >
                    <Icon type="plus" tooltip={{ title: translate('add') }} />
                </div>
            </div>
            {!!showRepeatEventStrategy && (
                <div className={styles.nextPage}>
                    <Dropdown
                        wrapperWidth={false}
                        label={translate('repeatEventStrategy')}
                        value={p_.page.repeatedEventHandlingStrategy || 'reload'}
                        valueField="id"
                        options={[
                            {
                                id: 'ignore',
                                name: translate('ignore'),
                            },
                            {
                                id: 'reload',
                                name: translate('reload'),
                            },
                        ]}
                        onChange={(item) => {
                            //@ts-ignore
                            editor.setPageRepeatEventHandlingStrategy(item.id)
                        }}
                        centered
                    />
                </div>
            )}
            {p_.pages.length > 1 && (
                <div className={styles.nextPage}>
                    <Dropdown
                        wrapperWidth={false}
                        label={translate('nextPageLink')}
                        value={p_.page.nextPageOrder}
                        options={getPageOptions(p_.pages)}
                        onChange={onChangePage}
                        centered
                    />
                </div>
            )}
            {!(p_.page.broadcastEvents && p_.page.broadcastEvents.length) && !p_.page.playOnEvent && (
                <div className={styles.emptyMessage}>{translate('listOfEventsIsEmpty')}</div>
            )}
            {p_.page.broadcastEvents &&
                p_.page.broadcastEvents.map((event: { id: number; name: string }) => (
                    <div className={styles.eventItem}>
                        <div>{event.name}</div>
                        <div onClick={() => deleteEvent(event.id)}>
                            <Icon type={'delete'} size={16} />
                        </div>
                    </div>
                ))}
            {p_.page.playOnEvent &&
                p_.page.playOnEvent.events.map((event, index) => (
                    <ListItem
                        key={index}
                        type={event.eventType}
                        condition={event.eventCondition}
                        onDelete={() => onDelete(index)}
                        onEdit={() => {
                            setState((prevState: any) => {
                                return {
                                    ...prevState,
                                    event: {
                                        key: event.eventCondition.key,
                                        id: event.eventType,
                                        eventIndex: index,
                                        eventCondition: event.eventCondition,
                                    },
                                    isOpenModal: true,
                                    createMode: false,
                                }
                            })
                        }}
                    />
                ))}
            <TouchEditor
                createMode={s_.createMode}
                open={s_.isOpenModal}
                pages={p_.pages}
                page={p_.page}
                event={s_.event}
                onSave={onSave}
                onClose={() =>
                    setState((prevState) => {
                        return {
                            ...prevState,
                            isOpenModal: false,
                            event: undefined,
                        }
                    })
                }
                type="page"
                action={{}}
                onSelectEvent={onSaveSelectedEvent}
            />
        </div>
    )
}

export default EditorEvents
